import { RouteError } from '@/components/route-error'
import { RoutePending } from '@/components/route-pending'
import { useEffect,useState,useCallback,useMemo } from "react"
import { requireCapability } from '@/lib/auth'
import { getStoreCurrency } from '@/lib/formatters'
import { createFileRoute,Outlet,useNavigate,useSearch } from "@tanstack/react-router"
import { z } from "zod"
import { api,type AxiosError,type MaybeEnveloped } from '@/lib/api-client'
import { useOrganizationShop } from "@/hooks/use-organization-shop"
import { toast } from "sonner"
import {
Table,
TableBody,
TableCell,
TableHead,
TableHeader,
TableRow,
} from "@/components/ui/table"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import { Search,MoreHorizontal,Plus,RefreshCw,Shield,ShieldOff,X,Trash2,Pencil,Settings,Package,Clipboard,Printer as PrinterIcon } from "lucide-react"
import { Button } from "@/components/ui/button"
import { PageBreadcrumb } from "@/components/dashboard/page-breadcrumb"
import { DataTablePagination } from '@/components/dashboard/data-table-pagination'
import { InventoryEmptyState } from '@/components/dashboard/inventory-empty-state'
import * as React from "react"
import { Skeleton } from "@/components/ui/skeleton"
import { ImageWithSkeleton } from "@/components/ui/image-with-skeleton"
import { cn } from "@/lib/utils"
import { SaleConfig,UnifiedInventoryItem,Category } from "@/types"
import {
HoverCard,
HoverCardContent,
HoverCardTrigger,
} from "@/components/ui/hover-card"
import {
Tooltip,
TooltipContent,
TooltipProvider,
TooltipTrigger,
} from "@/components/ui/tooltip"
import { SuspensionAuditHoverCard } from "@/features/catalog/suspension-audit-hover-card"
import { DropdownMenu,DropdownMenuContent,DropdownMenuItem,DropdownMenuSeparator,DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import { KeptForAuditNote } from "@/features/inventory/kept-for-audit-note"
import { Checkbox } from "@/components/ui/checkbox"
import { extractArray } from "@/features/http"
import { useQuery,useQueryClient } from "@tanstack/react-query"
import { queryKeys } from "@/lib/query-keys"
import { lazy } from "react"
import { HoldDialog,ReleaseHoldDialog } from "@/features/inventory/stock-dialogs"
import { DeleteConfirmationDialog } from '@/components/delete-confirmation-dialog'
const StockSidePanel = React.lazy(() => import("@/features/inventory/stock-side-panel").then(m => ({ default: m.StockSidePanel })))
const AdjustLocalInventoryDialog = React.lazy(() => import("@/features/inventory/dialogs/adjust-local-inventory-dialog").then(m => ({ default: m.AdjustLocalInventoryDialog })))
const EditLocalInventoryDialog = React.lazy(() => import("@/features/inventory/dialogs/edit-local-inventory-dialog").then(m => ({ default: m.EditLocalInventoryDialog })))
const BulkAdjustLocalInventoryDialog = React.lazy(() => import("@/features/inventory/dialogs/bulk-adjust-local-inventory-dialog").then(m => ({ default: m.BulkAdjustLocalInventoryDialog })))
const PrintLabelDialog = React.lazy(() => import("@/features/inventory/print-label-dialog").then(m => ({ default: m.PrintLabelDialog })))

const AddInventoryDialog = lazy(() =>
  import("@/features/inventory/dialogs/add-inventory-dialog").then(m => ({ default: m.AddInventoryDialog }))
)

const searchSchema = z.object({
  page: z.number().catch(1),
  limit: z.number().catch(20),
  q: z.string().catch(''),
  track: z.string().optional(),
})

type StockSearch = z.infer<typeof searchSchema>

export const Route = createFileRoute('/_dashboard/inventory/stock')({
  pendingComponent: RoutePending,
  errorComponent: RouteError,
  beforeLoad: requireCapability('MANAGE_CATALOG'),
  validateSearch: searchSchema,
  component: StockLayout,
})

let suppressedStockAutoOpenSignature: string | null = null

function StockLayout() {
  return <Outlet />
}

interface UnsellableReason {
  code: string
}

interface ExtendedAvailability {
  state?: string
  stockStatus?: string
  availableQuantity?: number
  blockedQuantity?: number
  blockReasons?: string[]
  unsellableReasons?: UnsellableReason[]
}

export function StockPage({ selectedId }: { selectedId: string | undefined }) {
  const { shopId, shop, isLoading: shopLoading } = useOrganizationShop()
  const queryClient = useQueryClient()

  const navigate = useNavigate()
  const { page: currentPage, limit, q: searchQuery, track: urlTrack } = useSearch({ from: Route.id }) as StockSearch

  const isMetrcEnabled = shop?.isMetrcEnabled ?? true
  const trackFilter = (!isMetrcEnabled) ? "local" : (urlTrack || "all")
  const pageSignature = `${shopId ?? 'no-shop'}:${currentPage}:${limit}:${searchQuery}:${trackFilter}`

  useEffect(() => {
    if (!isMetrcEnabled && urlTrack !== 'local') {
      navigate({ to: '.', search: (prev) => ({ ...prev, track: 'local' }), replace: true })
    }
  }, [isMetrcEnabled, urlTrack, navigate, currentPage, limit, searchQuery, selectedId])

  const [debouncedSearch, setDebouncedSearch] = useState(searchQuery)

  const [adjustOpen, setAdjustOpen] = useState(false)
  const [adjustType, setAdjustType] = useState<"ADJUSTMENT" | "RECEIVING" | "RECOUNT" | "WASTE">("ADJUSTMENT")
  const [adjustRecord, setAdjustRecord] = useState<{ id: string; isTrackingEnabled: boolean } | null>(null)
  const [sidePanelRefreshKey, setSidePanelRefreshKey] = useState(0)

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [bulkAdjustOpen, setBulkAdjustOpen] = useState(false)
  const [bulkAdjustType, setBulkAdjustType] = useState<"ADJUSTMENT" | "RECEIVING" | "WASTE">("ADJUSTMENT")
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [deleteIds, setDeleteIds] = useState<string[]>([])
  const [editOpen, setEditOpen] = useState(false)
  const [editRecord, setEditRecord] = useState<{ id: string; isTrackingEnabled: boolean; lowStockThreshold: number } | null>(null)
  const [addAllOpen, setAddAllOpen] = useState(false)
  const [isRefreshing, setIsRefreshing] = useState(false)

  const [bulkHoldOpen, setBulkHoldOpen] = useState(false)
  const [bulkHoldReason, setBulkHoldReason] = useState("")
  const [isBulkHolding, setIsBulkHolding] = useState(false)
  const [bulkReleaseOpen, setBulkReleaseOpen] = useState(false)
  const [bulkReleaseNote, setBulkReleaseNote] = useState("")
  const [isBulkUnholding, setIsBulkUnholding] = useState(false)

  const [printLabelOpen, setPrintLabelOpen] = useState(false)
  const [printLabelItem, setPrintLabelItem] = useState<UnifiedInventoryItem | null>(null)

  const setSelectedId = useCallback((id: string | null) => {
    if (id) {
      navigate({
        to: '/inventory/stock/$item',
        params: { item: id },
        search: (prev) => ({ ...prev }),
      })
      return
    }

    navigate({ to: '/inventory/stock', search: (prev) => ({ ...prev }) })
  }, [navigate])

  const { data: categoriesMap = {} } = useQuery({
    queryKey: queryKeys.categories(shopId || ''),
    queryFn: async () => {
      const response = await api.get(`/categories?limit=100`)
      const data = extractArray<Category>(response)
      const map: Record<string, Category> = {}
      data.forEach((c) => { if (c.id) map[c.id] = c })
      return map
    },
    enabled: !!shopId
  })

  const { data: stockResponse, isLoading: stockLoading, refetch: fetchStock } = useQuery({
    queryKey: queryKeys.stock(shopId || '', { page: currentPage, limit, search: debouncedSearch, track: trackFilter }),
    queryFn: async () => {
      const params: Record<string, string | number> = { shopId: shopId!, page: currentPage, limit }
      if (debouncedSearch) params.search = debouncedSearch
      if (trackFilter && trackFilter !== "all") params.track = trackFilter
      const response = await api.get(`/inventory/unified`, { params })
      return response
    },
    enabled: !!shopId
  })

  const items = useMemo(() => extractArray<UnifiedInventoryItem>(stockResponse) || [], [stockResponse])
  const stockMeta = (stockResponse as { meta?: { total?: number; totalPages?: number } } | undefined)?.meta
  const totalItems = stockMeta?.total || items.length
  const totalPages = stockMeta?.totalPages || 1
  const loading = stockLoading || shopLoading

  const currentPageHasSelectedItem =
    !!selectedId && items.some((item) => item.sku === selectedId || item.saleConfigId === selectedId)
  const effectiveSelectedId = (() => {
    if (currentPageHasSelectedItem) return selectedId
    if (loading || items.length === 0) return undefined
    if (!selectedId && suppressedStockAutoOpenSignature === pageSignature) return undefined
    return items[0].sku || items[0].saleConfigId
  })()

  // Sale-config details for the visible rows — the query cache replaces the old
  // module-level Map (same persistence across remounts, plus invalidation).
  // Price labels, tier hovers and strain chips render straight off the
  // unified rows (server-enriched) — the old per-row GET /sale-configs/:id
  // fan-out (N+1) is gone. Only the SELECTED row's full sale-config is
  // fetched, on demand, for the side panel.
  const selectedRowConfigId = useMemo(() => {
    const row = items.find((item) => item.sku === effectiveSelectedId || item.saleConfigId === effectiveSelectedId)
    return row?.saleConfigId ?? null
  }, [items, effectiveSelectedId])
  const selectedConfigQuery = useQuery({
    queryKey: queryKeys.saleConfigDetail(selectedRowConfigId ?? 'idle'),
    queryFn: async () => {
      const response = await api.get<MaybeEnveloped<SaleConfig>>(`/sale-configs/${selectedRowConfigId}`)
      // Cast: legacy envelope unwrap, same as the side panels.
      return (response?.data || response) as SaleConfig
    },
    enabled: !!selectedRowConfigId,
    staleTime: 5 * 60_000,
    retry: false,
  })
  const saleConfigDetails = useMemo<Record<string, SaleConfig>>(
    () => (selectedConfigQuery.data?.id ? { [selectedConfigQuery.data.id]: selectedConfigQuery.data } : {}),
    [selectedConfigQuery.data],
  )

  const selectedItem = items.find(item => item.sku === effectiveSelectedId || item.saleConfigId === effectiveSelectedId) || null
  // Only records with no history beyond creation can be deleted; the rest of a selection is left alone.
  const deletableSelected = items.flatMap((i) => (i.localInventoryId && i.deletable && selectedIds.has(i.localInventoryId) ? [i.localInventoryId] : []))

  const closeSelectedItem = useCallback(() => {
    suppressedStockAutoOpenSignature = pageSignature
    setSelectedId(null)
  }, [pageSignature, setSelectedId])

  useEffect(() => {
    if (!effectiveSelectedId || effectiveSelectedId === selectedId) return
    setSelectedId(effectiveSelectedId)
  }, [effectiveSelectedId, selectedId, setSelectedId])

  useEffect(() => {
    return () => {
      window.setTimeout(() => {
        if (!window.location.pathname.startsWith('/inventory/stock')) {
          suppressedStockAutoOpenSignature = null
        }
      }, 0)
    }
  }, [])

  const handleSearchChange = (val: string) => {
    navigate({ to: '.', search: (prev) => ({ ...prev, q: val || undefined, page: 1 }), replace: true })
  }

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery || ""), 500)
    return () => clearTimeout(timer)
  }, [searchQuery])

  const handleBulkPlaceHold = async () => {
    if (!bulkHoldReason.trim() || selectedIds.size === 0) return
    setIsBulkHolding(true)
    try {
      const results = await Promise.allSettled(
        Array.from(selectedIds).map(id =>
          api.post(`/inventory/${id}/hold`, { reason: bulkHoldReason.trim() })
        )
      )
      const failed = results.filter(r => r.status === 'rejected').length
      const succeeded = results.filter(r => r.status === 'fulfilled').length
      if (failed > 0) {
        toast.error(`Hold placed on ${succeeded} record(s), ${failed} failed.`)
      } else {
        toast.success(`Successfully placed ${succeeded} record(s) on hold.`)
      }
      setBulkHoldOpen(false)
      setBulkHoldReason('')
      setSelectedIds(new Set())
      fetchStock()
      setSidePanelRefreshKey(prev => prev + 1)
    } catch (error: unknown) {
      toast.error((error as AxiosError<{message: string}>).response?.data?.message || 'Error')
    } finally {
      setIsBulkHolding(false)
    }
  }

  const handleBulkReleaseHold = async () => {
    if (selectedIds.size === 0) return
    setIsBulkUnholding(true)
    try {
      const results = await Promise.allSettled(
        Array.from(selectedIds).map(id =>
          api.delete(`/inventory/${id}/hold`, { data: bulkReleaseNote.trim() ? { note: bulkReleaseNote.trim() } : undefined })
        )
      )
      const failed = results.filter(r => r.status === 'rejected').length
      const succeeded = results.filter(r => r.status === 'fulfilled').length
      if (failed > 0) {
        toast.error(`Hold released on ${succeeded} record(s), ${failed} failed.`)
      } else {
        toast.success(`Successfully released hold on ${succeeded} record(s).`)
      }
      setBulkReleaseOpen(false)
      setBulkReleaseNote('')
      setSelectedIds(new Set())
      fetchStock()
      setSidePanelRefreshKey(prev => prev + 1)
    } catch (error: unknown) {
      toast.error((error as AxiosError<{message: string}>).response?.data?.message || 'Error')
    } finally {
      setIsBulkUnholding(false)
    }
  }

  const handleTrackFilter = (value: string) => {
    navigate({ to: '.', search: (prev) => ({ ...prev, page: 1, track: value === "all" ? undefined : value }) })
  }

  const localItems = items.filter(item => item.track === "local" && item.localInventoryId)

  const toggleSelectAll = () => {
    if (selectedIds.size === localItems.length && localItems.length > 0) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(localItems.map(item => item.localInventoryId!)))
    }
  }

  const toggleSelect = (id: string) => {
    const next = new Set(selectedIds)
    if (next.has(id)) {
      next.delete(id)
    } else {
      next.add(id)
    }
    setSelectedIds(next)
  }

  const getStatusDot = (item: UnifiedInventoryItem): { color: string; label: string } => {
    const availability = item.availability as ExtendedAvailability | undefined
    const status = item.status?.toLowerCase()

    if (availability) {
      const state = availability.state || availability.stockStatus
      const availableQty = availability.availableQuantity ?? 0
      const blockedQty = availability.blockedQuantity ?? 0
      
      const reasons = availability.blockReasons || []
      const localReasons = (availability.unsellableReasons || []).map((r: UnsellableReason) => r.code)
      const combinedCodes = [...reasons, ...localReasons]

      if (combinedCodes.includes('PRODUCT_SUSPENDED')) return { color: "bg-red-600", label: "Product Suspended" }
      if (combinedCodes.includes('SALE_CONFIG_SUSPENDED')) return { color: "bg-red-600", label: "Configuration Suspended" }

      if (state?.toUpperCase() === 'AVAILABLE' || availableQty > 0) {
        if (blockedQty > 0 || combinedCodes.some((c: string) => c.includes('HOLD'))) {
          return { color: "bg-emerald-500/60 border border-amber-500", label: "Partially Blocked" }
        }
        if (status === 'low_stock') return { color: "bg-amber-500", label: "Low Stock" }
        return { color: "bg-emerald-500", label: "In Stock" }
      }

      if (state?.toUpperCase() === 'BLOCKED' || state?.toUpperCase() === 'UNAVAILABLE' || (availableQty === 0 && (blockedQty > 0 || combinedCodes.some((c: string) => c.includes('HOLD'))))) {
        return { color: "bg-amber-600", label: "Fully Blocked" }
      }

      if (availableQty === 0 && blockedQty === 0) {
        return { color: "bg-red-500", label: "Out of Stock" }
      }
    }

    if (status === "in_stock") return { color: "bg-emerald-500", label: "In Stock" }
    if (status === "low_stock") return { color: "bg-amber-500", label: "Low Stock" }
    if (status === "out_of_stock" || status === "no_stock") return { color: "bg-red-500", label: "Out of Stock" }
    if (status === "blocked" || status === "unavailable") return { color: "bg-amber-600", label: "Fully Blocked" }
    if (status === "untracked") return { color: "bg-muted-foreground/60", label: "Untracked" }

    return { color: "bg-muted-foreground/40", label: item.status || 'Unknown' }
  }

  const getPriceLabel = (item: UnifiedInventoryItem) => {
    const tiers = item.pricingTiers ?? []
    const uom = item.uom || (item.sellType === "WEIGHT" ? "g" : "ea")
    const displayUom = uom === 'pc' ? 'ea' : uom
    if (tiers.length === 0) {
      if (!item.pricingSchemeName) return "—"
      return <Badge variant="secondary" className="font-mono text-xs">{item.pricingSchemeName}{displayUom ? `/${displayUom}` : ""}</Badge>
    }
    const money = (cents: number) =>
      new Intl.NumberFormat("en-US", { style: "currency", currency: getStoreCurrency() }).format(cents / 100)
    if (item.pricingSchemeType === "TIERED" && tiers.length > 1) {
      const minPrice = Math.min(...tiers.map((t) => t.unitPriceCents))
      return <Badge variant="secondary" className="font-mono text-xs">From {money(minPrice)}/{displayUom}</Badge>
    }
    return <Badge variant="secondary" className="font-mono text-xs">{money(tiers[0].unitPriceCents)}/{displayUom}</Badge>
  }

  return (
    <div className="space-y-6 h-full">
      <PageBreadcrumb />

      <div className="flex items-center justify-between space-y-2">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Sellable Inventory</h2>
          <p className="text-muted-foreground mt-1">What’s available to sell at {shop?.shopName || 'this shop'}.</p>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative max-w-sm w-full group">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            type="search"
            placeholder="Search inventory..."
            className="pl-9 pr-8"
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
          />
          {searchQuery && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => handleSearchChange('')}
              className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 text-muted-foreground hover:bg-muted hover:text-foreground transition-all"
            >
              <X className="h-4 w-4" />
              <span className="sr-only">Clear search</span>
            </Button>
          )}
        </div>
        <Button
          variant="outline"
          size="icon"
          className="h-9 w-9 shrink-0"
          title="Refresh"
          disabled={isRefreshing}
          onClick={() => {
            setIsRefreshing(true)
            fetchStock()
            setTimeout(() => setIsRefreshing(false), 600)
          }}
        >
          <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
        </Button>
        {isMetrcEnabled && (
          <div className="flex items-center gap-1 rounded-md border p-1">
            {(["all", "metrc", "local"] as const).map((val) => (
              <Button
                key={val}
                size="sm"
                variant={trackFilter === val ? "secondary" : "ghost"}
                className="h-7 px-3 text-xs capitalize"
                onClick={() => handleTrackFilter(val)}
              >
                {val === "all" ? "All" : val === "metrc" ? "Metrc" : "Local"}
              </Button>
            ))}
          </div>
        )}
        {(trackFilter === "local" || trackFilter === "all") && (
          <Button size="sm" className="ml-auto" onClick={() => setAddAllOpen(true)}>
            <Plus className="mr-2 h-4 w-4" /> Add Inventory
          </Button>
        )}
      </div>

      <div className="flex flex-col md:flex-row gap-6 items-start">
        <div className={`flex-1 min-w-0 transition-all duration-300 ${!loading && items.length === 0 ? "" : "border rounded-md squircle bg-card shadow-sm overflow-hidden"} ${selectedId ? "w-full md:w-[calc(100%-340px)] lg:w-[calc(100%-380px)]" : "w-full"}`}>
          {!loading && items.length === 0 ? (
            <InventoryEmptyState
              title="No sellable inventory found"
              description={
                isMetrcEnabled
                  ? 'Inventory will appear here once added manually or synced from Metrc.'
                  : 'Inventory will appear here once added manually.'
              }
            />
          ) : (
            <>
              <div className="overflow-auto max-h-[calc(100vh-14rem)]">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50 hover:bg-muted/50">
                      {trackFilter === "local" && (
                        <TableHead className="w-[40px] px-4">
                          <Checkbox checked={localItems.length > 0 && selectedIds.size === localItems.length} onCheckedChange={toggleSelectAll} />
                        </TableHead>
                      )}
                      <TableHead className="min-w-[300px]">Product</TableHead>
                      <TableHead className="w-[120px] text-right">Available</TableHead>
                      <TableHead className={cn("w-[140px]", selectedId ? "hidden lg:table-cell" : "")}>Price</TableHead>
                      {isMetrcEnabled && <TableHead className="w-[90px]">Track</TableHead>}
                      <TableHead className="w-[60px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading ? (
                      Array.from({ length: 6 }).map((_, i) => (
                        <TableRow key={i}>
                          {trackFilter === "local" && (
                            <TableCell className="px-4">
                              <Skeleton className="h-4 w-4 rounded" />
                            </TableCell>
                          )}
                          <TableCell className="min-w-[300px]">
                            <div className="flex items-center gap-3">
                              <Skeleton className="h-10 w-10 rounded-md shrink-0" />
                              <div className="flex flex-col gap-2">
                                <Skeleton className="h-4 w-40" />
                                <Skeleton className="h-3 w-24" />
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            <Skeleton className="h-4 w-16 ml-auto" />
                          </TableCell>
                          <TableCell className={selectedId ? "hidden lg:table-cell" : ""}>
                            <Skeleton className="h-4 w-20" />
                          </TableCell>
                          {isMetrcEnabled && (
                            <TableCell>
                              <Skeleton className="h-6 w-16 rounded-full" />
                            </TableCell>
                          )}
                          <TableCell>
                            <Skeleton className="h-8 w-8 ml-auto" />
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      items.map((item, index) => {
                        const isTracked = item.isTrackingEnabled || item.track === "metrc"
                        const avail = item.availability as ExtendedAvailability | undefined
                        const availableNum = isTracked ? (avail?.availableQuantity ?? item.available ?? 0) : null
                        const available = availableNum !== null ? availableNum : "--"
                        const blockedQty = avail?.blockedQuantity ?? 0
                        const uom = item.uom || "ea"
                        const displayUom = uom === "ea" ? "pc" : uom
                        
                        const reasons = avail?.blockReasons || []
                        const localReasons = (avail?.unsellableReasons || []).map((r: UnsellableReason) => r.code)
                        const combinedCodes = [...reasons, ...localReasons]

                        const priceLabel = getPriceLabel(item)
                        const rowTiers = item.pricingTiers ?? []
                        const statusInfo = getStatusDot(item)

                        return (
                          <TableRow key={`${item.saleConfigId}-${index}`} className={cn("cursor-pointer group hover:bg-muted/50 transition-colors", effectiveSelectedId === (item.sku || item.saleConfigId) && "bg-muted/80")} onClick={() => {
                            const rowId = item.sku || item.saleConfigId
                            if (!rowId) return
                            if (effectiveSelectedId === rowId) {
                              closeSelectedItem()
                              return
                            }
                            setSelectedId(rowId)
                          }}>
                            {item.track === "local" && item.localInventoryId && trackFilter === "local" ? (
                              <TableCell className="px-4" onClick={(e) => e.stopPropagation()}>
                                <Checkbox checked={selectedIds.has(item.localInventoryId)} onCheckedChange={() => toggleSelect(item.localInventoryId!)} />
                              </TableCell>
                            ) : trackFilter === "local" ? (
                              <TableCell className="px-4" />
                            ) : null}
                            <TableCell className="min-w-[300px]">
                              <div className="flex items-center gap-3">
                                <div className="h-10 w-10 rounded-md flex items-center justify-center overflow-hidden bg-muted/50 shrink-0">
                                  <ImageWithSkeleton
                                    src={item.imageUrl || "/placeholder-light/Place%20Holder%20Light%204_4.png"}
                                    alt={item.productName || "Product"}
                                    className="h-full w-full object-cover"
                                  />
                                </div>
                                <div className="flex flex-col min-w-0">
                                  <div className="flex items-center gap-1.5 overflow-hidden">
                                    <TooltipProvider delayDuration={400}>
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <div className={cn("font-medium truncate cursor-help", selectedItem ? "max-w-[200px]" : "max-w-[600px]")}>{item.productName}</div>
                                        </TooltipTrigger>
                                        <TooltipContent side="top" align="start">
                                          <p className="max-w-xs break-words">{item.productName}</p>
                                        </TooltipContent>
                                      </Tooltip>
                                    </TooltipProvider>
                                    <TooltipProvider delayDuration={300}>
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <div className={`h-2 w-2 rounded-full shrink-0 cursor-help ${statusInfo.color}`} />
                                        </TooltipTrigger>
                                        <TooltipContent side="top">
                                          {statusInfo.label}
                                        </TooltipContent>
                                      </Tooltip>
                                    </TooltipProvider>
                                    {item.track === 'local' && (item.isOnHold || combinedCodes.some((c: string) => c.includes('HOLD'))) && (
                                      <SuspensionAuditHoverCard id={item.localInventoryId!} type="inventory" label="Hold" />
                                    )}
                                  </div>
                                  {(item.displayName || item.strainName) && (
                                    <div className="text-xs text-muted-foreground truncate mt-0.5">
                                      {item.displayName}
                                      {item.strainName && (
                                        <span className={item.displayName ? "ml-1.5" : ""}>
                                          {item.displayName ? "· " : ""}{item.strainName}
                                          {item.strainType ? ` (${item.strainType.toLowerCase()})` : ""}
                                        </span>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </TableCell>
                            <TableCell className="text-right font-medium font-mono">
                              <TooltipProvider delayDuration={200}>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <div className={`inline-flex flex-col items-end cursor-help ${blockedQty > 0 ? "text-amber-600 dark:text-amber-500" : ""}`}>
                                      <div>{available}<span className="text-xs font-normal ml-0.5">{displayUom}</span></div>
                                    </div>
                                  </TooltipTrigger>
                                  <TooltipContent side="top" className="text-xs">
                                    <div className="space-y-1">
                                      <div className="flex items-center gap-2">
                                        <span className="text-muted-foreground">Available:</span>
                                        <span className="font-mono font-medium">{available} {displayUom}</span>
                                      </div>
                                      <div className="flex items-center gap-2">
                                        <span className={blockedQty > 0 ? "text-amber-400" : "text-muted-foreground"}>Blocked:</span>
                                        <span className={`font-mono font-medium ${blockedQty > 0 ? "text-amber-400" : ""}`}>{blockedQty} {displayUom}</span>
                                      </div>
                                    </div>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            </TableCell>
                            <TableCell className={cn(selectedItem ? "hidden lg:table-cell" : "")}>
                              {rowTiers.length > 1 ? (
                                <HoverCard>
                                  <HoverCardTrigger asChild>
                                    <div className="inline-flex cursor-help">
                                      {priceLabel}
                                    </div>
                                  </HoverCardTrigger>
                                  <HoverCardContent className="w-64 p-3" align="start">
                                    <p className="mb-2 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">Price tiers</p>
                                    <div className="space-y-1 max-h-[260px] overflow-y-auto">
                                      {rowTiers.map((tier, i) => (
                                        <div key={i} className="flex items-center justify-between text-sm">
                                          <span className="text-muted-foreground">
                                            {tier.startQuantity}+ {item.uom === 'pc' ? 'ea' : item.uom || ''}
                                          </span>
                                          <span className="font-mono tabular-nums">
                                            {new Intl.NumberFormat("en-US", { style: "currency", currency: getStoreCurrency() }).format(tier.unitPriceCents / 100)}
                                          </span>
                                        </div>
                                      ))}
                                    </div>
                                  </HoverCardContent>
                                </HoverCard>
                              ) : (
                                priceLabel
                              )}
                            </TableCell>
                            {isMetrcEnabled && (
                              <TableCell>
                                <Badge variant={item.track === "metrc" ? "default" : "secondary"} className="font-normal text-[10px] uppercase">{item.track}</Badge>
                              </TableCell>
                            )}
                            <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                              {item.track === "local" && item.localInventoryId && (
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" size="icon" className="h-8 w-8 opacity-100 xl:opacity-0 xl:group-hover:opacity-100 transition-opacity"><MoreHorizontal className="h-4 w-4" /></Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end" className="w-44">
                                    <DropdownMenuItem className="gap-1.5" onClick={() => { setAdjustRecord({ id: item.localInventoryId!, isTrackingEnabled: item.isTrackingEnabled ?? false }); setAdjustType("ADJUSTMENT"); setAdjustOpen(true) }}>
                                      <Settings className="h-4 w-4" />
                                      Adjust Quantity
                                    </DropdownMenuItem>
                                    <DropdownMenuItem className="gap-1.5" onClick={() => { setAdjustRecord({ id: item.localInventoryId!, isTrackingEnabled: item.isTrackingEnabled ?? false }); setAdjustType("RECEIVING"); setAdjustOpen(true) }}>
                                      <Package className="h-4 w-4" />
                                      Receive Stock
                                    </DropdownMenuItem>
                                    <DropdownMenuItem className="gap-1.5" onClick={() => { setAdjustRecord({ id: item.localInventoryId!, isTrackingEnabled: item.isTrackingEnabled ?? false }); setAdjustType("RECOUNT"); setAdjustOpen(true) }}>
                                      <Clipboard className="h-4 w-4" />
                                      Recount
                                    </DropdownMenuItem>
                                    <DropdownMenuItem className="gap-1.5" onClick={() => { setAdjustRecord({ id: item.localInventoryId!, isTrackingEnabled: item.isTrackingEnabled ?? false }); setAdjustType("WASTE"); setAdjustOpen(true) }}>
                                      <Trash2 className="h-4 w-4" />
                                      Mark as Waste
                                    </DropdownMenuItem>

                                    {item.track === 'local' && (
                                      item.isOnHold || combinedCodes.some((c: string) => c.includes('HOLD')) ? (
                                        <DropdownMenuItem className="gap-1.5" onClick={() => { setSelectedIds(new Set([item.localInventoryId!])); setBulkReleaseOpen(true) }}>
                                          <ShieldOff className="h-4 w-4" />
                                          Release Hold
                                        </DropdownMenuItem>
                                      ) : (
                                        <DropdownMenuItem className="gap-1.5" onClick={() => { setSelectedIds(new Set([item.localInventoryId!])); setBulkHoldOpen(true) }}>
                                          <Shield className="h-4 w-4" />
                                          Place on Hold
                                        </DropdownMenuItem>
                                      )
                                    )}

                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem className="gap-1.5" onClick={() => { setPrintLabelItem(item); setPrintLabelOpen(true) }}>
                                      <PrinterIcon className="h-4 w-4" />
                                      Print Label
                                    </DropdownMenuItem>

                                    <DropdownMenuItem className="gap-1.5" onClick={() => { setEditRecord({ id: item.localInventoryId || '', isTrackingEnabled: item.isTrackingEnabled || false, lowStockThreshold: item.lowStockThreshold ?? 0 }); setEditOpen(true) }}>
                                      <Pencil className="h-4 w-4" />
                                      Edit Settings
                                    </DropdownMenuItem>
                                    {item.deletable ? (
                                      <DropdownMenuItem className="gap-1.5 text-red-600 focus:text-red-700 focus:bg-red-50" onClick={() => { setDeleteIds([item.localInventoryId!]); setDeleteOpen(true) }}>
                                        <Trash2 className="h-4 w-4" />
                                        Delete Record
                                      </DropdownMenuItem>
                                    ) : <KeptForAuditNote />}
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              )}
                              {item.track === "metrc" && (
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" size="icon" className="h-8 w-8 opacity-100 xl:opacity-0 xl:group-hover:opacity-100 transition-opacity"><MoreHorizontal className="h-4 w-4" /></Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end" className="w-44">
                                    <DropdownMenuItem className="gap-1.5" onClick={() => { setPrintLabelItem(item); setPrintLabelOpen(true) }}>
                                      <PrinterIcon className="h-4 w-4" />
                                      Print Label
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              )}
                            </TableCell>
                          </TableRow>
                        )
                      })
                    )}
                  </TableBody>
                </Table>
              </div>
              <DataTablePagination
                page={currentPage}
                totalPages={totalPages}
                totalItems={totalItems}
                itemsPerPage={limit}
                isLoading={loading}
                entityName="items"
                onPageChange={(p: number) =>
                  navigate({ to: '.', search: (prev) => ({ ...prev, page: p }), replace: true })
                }
                onLimitChange={(l: number) =>
                  navigate({
                    to: '.',
                    search: (prev) => ({ ...prev, limit: l, page: 1 }),
                    replace: true,
                  })
                }
              >
                {selectedIds.size > 0 && (
                  <div className="flex items-center gap-2 mr-4 border-r pr-4">
                    <span className="text-xs text-muted-foreground whitespace-nowrap">Selected: {selectedIds.size}</span>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="sm" className="h-7 text-xs px-2 gap-1.5">
                          Bulk Actions <MoreHorizontal className="h-3.5 w-3.5" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="start" className="w-48">
                        <DropdownMenuItem className="gap-1.5" onClick={() => { setBulkAdjustType("ADJUSTMENT"); setBulkAdjustOpen(true) }}>
                          Adjust Quantity
                        </DropdownMenuItem>
                        <DropdownMenuItem className="gap-1.5" onClick={() => { setBulkAdjustType("RECEIVING"); setBulkAdjustOpen(true) }}>
                          Receive Stock
                        </DropdownMenuItem>
                        <DropdownMenuItem className="gap-1.5" onClick={() => { setBulkAdjustType("WASTE"); setBulkAdjustOpen(true) }}>
                          Mark as Waste
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem className="gap-1.5" onClick={() => setBulkHoldOpen(true)}>
                          <Shield className="h-4 w-4" />Place on Hold
                        </DropdownMenuItem>
                        <DropdownMenuItem className="gap-1.5" onClick={() => setBulkReleaseOpen(true)}>
                          <ShieldOff className="h-4 w-4" />Release Hold
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem 
                          className="gap-1.5 text-red-600 focus:text-red-600 focus:bg-red-50 dark:focus:bg-red-950/30" 
                          disabled={deletableSelected.length === 0}
                          onClick={() => { setDeleteIds(deletableSelected); setDeleteOpen(true) }}
                        >
                          <Trash2 className="h-4 w-4" /> {deletableSelected.length === selectedIds.size ? 'Delete Records' : `Delete ${deletableSelected.length} of ${selectedIds.size}`}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                )}
              </DataTablePagination>
            </>
          )}
        </div>

        {selectedItem && (
          <div className="squircle mx-0.5 mb-0.5 w-full md:w-[320px] lg:w-[360px] sticky top-0 shrink-0 mt-0 flex flex-col h-fit max-h-[calc(100vh-6.5rem)]">
            <React.Suspense fallback={null}>
              <StockSidePanel
                open={!!selectedItem}
                onOpenChange={(open: boolean) => !open && closeSelectedItem()}
                item={selectedItem}
                saleConfigDetails={saleConfigDetails}
                categoriesMap={categoriesMap}
                refreshKey={sidePanelRefreshKey}
                onAction={selectedItem?.track === "local" && selectedItem?.localInventoryId ? (type: "ADJUSTMENT" | "RECEIVING" | "RECOUNT" | "WASTE") => {
                  setAdjustRecord({ id: selectedItem.localInventoryId!, isTrackingEnabled: selectedItem.isTrackingEnabled ?? false })
                  setAdjustType(type)
                  setAdjustOpen(true)
                } : undefined}
                onEdit={selectedItem?.track === "local" && selectedItem?.localInventoryId ? () => {
                  setEditRecord({ id: selectedItem.localInventoryId!, isTrackingEnabled: selectedItem.isTrackingEnabled || false, lowStockThreshold: selectedItem.lowStockThreshold ?? 0 })
                  setEditOpen(true)
                } : undefined}
                onDelete={selectedItem?.track === "local" && selectedItem?.localInventoryId && selectedItem.deletable ? () => {
                  setDeleteIds([selectedItem.localInventoryId!])
                  setDeleteOpen(true)
                } : undefined}
                onPrint={selectedItem ? () => {
                  setPrintLabelItem(selectedItem)
                  setPrintLabelOpen(true)
                } : undefined}
                onRefresh={() => {
                  if (selectedItem?.saleConfigId) {
                    queryClient.invalidateQueries({ queryKey: queryKeys.saleConfigDetail(selectedItem.saleConfigId) })
                  }
                  fetchStock()
                  setSidePanelRefreshKey(prev => prev + 1)
                }}
              />
            </React.Suspense>
          </div>
        )}
      </div>

      <React.Suspense fallback={null}>
        <AdjustLocalInventoryDialog open={adjustOpen} onOpenChange={setAdjustOpen} record={adjustRecord} defaultType={adjustType} onComplete={() => { setAdjustOpen(false); fetchStock(); setSidePanelRefreshKey(prev => prev + 1) }} />
      </React.Suspense>
      <React.Suspense fallback={null}>
        <BulkAdjustLocalInventoryDialog open={bulkAdjustOpen} onOpenChange={setBulkAdjustOpen} records={items.filter(item => item.localInventoryId && selectedIds.has(item.localInventoryId)).map(item => ({ id: item.localInventoryId!, quantity: (item).available ?? 0, isTrackingEnabled: item.isTrackingEnabled ?? false, productName: item.productName || '', sku: item.sku || '', unitOfMeasureAbbr: item.uom || '' }))} defaultType={bulkAdjustType} onComplete={() => { setBulkAdjustOpen(false); fetchStock(); setSelectedIds(new Set()); setSidePanelRefreshKey(prev => prev + 1) }} />
      </React.Suspense>
      <React.Suspense fallback={null}>
        <EditLocalInventoryDialog open={editOpen} onOpenChange={setEditOpen} record={editRecord} onUpdated={() => { setEditOpen(false); fetchStock(); setSidePanelRefreshKey(prev => prev + 1) }} />
      </React.Suspense>
      <React.Suspense fallback={null}>
        <AddInventoryDialog open={addAllOpen} onOpenChange={setAddAllOpen} shopId={shopId || ''} onCreated={() => { setAddAllOpen(false); fetchStock() }} />
      </React.Suspense>

      <React.Suspense fallback={null}>
        {printLabelOpen && printLabelItem && (
          <PrintLabelDialog
            open={printLabelOpen}
            onOpenChange={setPrintLabelOpen}
            item={printLabelItem}
            shopId={shopId || ''}
          />
        )}
      </React.Suspense>

      <DeleteConfirmationDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title="Delete inventory record?"
        description="This removes the record. It has no sales or adjustments, so nothing in the audit trail is lost — the deletion itself is logged."
        onConfirm={async () => {
          // Each record on its own: one kept for its history mustn't hide the rest, and the server's reason is shown as-is.
          const results = await Promise.allSettled(deleteIds.map((id) => api.delete(`/inventory/${id}`)))
          const failed = results.flatMap((r) => (r.status === 'rejected' ? [r.reason as AxiosError<{ message?: string }>] : []))
          const deleted = results.length - failed.length
          if (deleted > 0) toast.success(`${deleted} inventory record${deleted === 1 ? '' : 's'} deleted.`)
          if (failed.length > 0) toast.error(failed.length === 1 && deleted === 0 ? "Can't delete this record" : `${failed.length} record${failed.length === 1 ? '' : 's'} not deleted`, { description: failed[0]?.response?.data?.message ?? 'Please try again.' })
          setSelectedIds(new Set())
          setDeleteIds([])
          setDeleteOpen(false)
          fetchStock()
        }}
        confirmLabel="Delete Record"
      />

      <HoldDialog
        open={bulkHoldOpen}
        onOpenChange={setBulkHoldOpen}
        reason={bulkHoldReason}
        onReasonChange={setBulkHoldReason}
        onConfirm={handleBulkPlaceHold}
        isSubmitting={isBulkHolding}
      />
      <ReleaseHoldDialog
        open={bulkReleaseOpen}
        onOpenChange={setBulkReleaseOpen}
        note={bulkReleaseNote}
        onNoteChange={setBulkReleaseNote}
        onConfirm={handleBulkReleaseHold}
        isSubmitting={isBulkUnholding}
      />
    </div>
  )
}
